import React, { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router';
import { useAtomValue, useSetAtom } from 'jotai';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import { Grid } from '@rocco/ui/layout/components/Grid';
import { Pagination as TilePagination } from '@rocco/ui/pagination/components/Pagination';
import type {
    TileableModel,
    TileViewModel,
} from '@rocco/ui/tile-shared/view-models/tile';
import { isArticleTileViewModel } from '@rocco/ui/tile-article/view-models/article';
import { useCategoryRevamp } from '@modules/category/hook/use-category-revamp';
import { getSearchTileAspect } from '@modules-rocco/search/util/get-search-tile-aspect';
import { Skeleton } from '@rocco/components/skeleton';
import { buildPaginationUrl } from '@modules-rocco/pagination/util/build-pagination-url';
import { NoMatchesFound } from '@rocco/ui/no-matches/components/NoMatchesFound';
// Remove incorrect import path
// import { CategoryPageTileWithAdminActions } from '~/ui-rocco/pages/category-page/CategoryPageTileWithAdminActions';
import { useTilesAdminData } from '~/modules-rocco/admin/hooks/use-tiles-admin-data';
import { tileSliderAtom } from '@rocco/states/tileSliderAtom';
import type { useCategoryFilterSort } from '@ui-rocco/hooks/use-category-filter-sort';
import { searchPageDataAtom } from '../states/search-page-data-atom';
import type { FilterTabViewModel } from '@rocco/ui/filter-sort/view-models/filter-tab';
import { useSearchTileClickTracking } from '~/ui-rocco/hooks/use-search-tile-click-tracking';

// Updated imports for extracted components
import { SearchPageGridHeader } from './SearchPageGridHeader';
import { SearchPageFilterTabs } from './SearchPageFilterTabs';
import { MobileSearchResultsAll } from './MobileSearchResultsAll';
import { SearchPageTile } from './SearchPageTile';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';

type SearchResultPageTilesGridProps = ReturnType<
    typeof useCategoryFilterSort
> & {
    isLoading: boolean;
};

/**
 * Renders the main grid of search results, including pagination and loading states.
 * Handles both desktop and mobile layouts, including the 'All' results view for mobile.
 */
export const SearchResultPageTilesGrid: React.FC<
    SearchResultPageTilesGridProps
> = (props) => {
    useSearchTileClickTracking();
    const pageDataFromAtom = useAtomValue(searchPageDataAtom);
    const results = pageDataFromAtom?.results;
    const pagination = pageDataFromAtom?.pagination ?? {
        page: 1,
        totalPages: 1,
        totalCount: 0,
    };
    const { mobile: isMobile } = useAppDisplayModeContext();
    const filterTabs = pageDataFromAtom?.filterTabs ?? [];
    const filterStates = pageDataFromAtom?.filterStates;

    const isLoadingFromProps = props.isLoading;

    const location = useLocation();
    const currentSearchParams = new URLSearchParams(location.search);
    const currentUrlSearchQuery = currentSearchParams.get('search') ?? '';

    const scrollToRef = useRef<HTMLDivElement | null>(null);
    const showMobileSearchResultsAll =
        isMobile &&
        filterTabs.find((tab: FilterTabViewModel) => tab.selected)?.id === 0;
    const setTileSlider = useSetAtom(tileSliderAtom);
    const { isInDesignBoard } = useHandleFollow();

    const currentFilterStates = filterStates ?? {
        visibilityType: 'search' as const,
        states: {},
    };
    const skeletonCount = isMobile ? 4 : 16;
    const categoryRevamp = useCategoryRevamp();

    const tiles: TileViewModel<TileableModel>[] = useMemo(
        () =>
            results?.map((result) => ({
                aspectRatio: getSearchTileAspect(result, categoryRevamp),
                model: {
                    ...result,
                    /*
                     * The editorial card the `/articles` directory draws, said
                     * outright — the same thing `searchArticles` stamps on for
                     * the in-page results there, off this same ES mapper.
                     *
                     * It is not only a different look. On `featured` the
                     * caption is laid over the picture and the hover panel is
                     * pinned to the whole card, so "Visit site" and Share land
                     * on top of the title and byline. The editorial variant
                     * stacks the caption under the picture and keeps the panel
                     * inside the frame, so nothing overlaps.
                     */
                    ...(isArticleTileViewModel(result) && {
                        variant: categoryRevamp
                            ? ('editorial' as const)
                            : ('featured' as const),
                    }),
                },
            })) ?? [],
        [results, categoryRevamp],
    );
    const tilesAdmin = useTilesAdminData(tiles);

    // Products browse is 2-up on mobile (compare-and-shop); other verticals
    // and the mixed grid stay 1-up, matching the browse pages' per-vertical
    // mobile columns.
    const isProductsResults = location.pathname.includes('category_products');
    const gridColClasses =
        categoryRevamp && isProductsResults
            ? 'grid-cols-2 md:grid-cols-2 lg:grid-cols-4 qhd:grid-cols-5'
            : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4 qhd:grid-cols-5';

    useEffect(() => {
        // Disable the tile slider if the mobile 'All' results view is active.
        setTileSlider((state) => ({
            ...state,
            enableTileSliderAll: !showMobileSearchResultsAll,
        }));
    }, [showMobileSearchResultsAll, setTileSlider]);

    return (
        <div className="px-0 md:px-12" ref={scrollToRef}>
            {isLoadingFromProps ? (
                <>
                    <div className="px-6 md:hidden">
                        <Skeleton className="h-12 w-full" variant="light" />
                        <div className="mt-6 flex gap-2">
                            <Skeleton className="h-12 w-1/3" variant="light" />
                            <Skeleton className="h-12 w-1/3" variant="light" />
                            <Skeleton className="h-12 w-1/3" variant="light" />
                        </div>
                    </div>
                    <div className="hidden md:block">
                        {(
                            currentFilterStates.states.category as {
                                category?: { title: string };
                            }
                        )?.category?.title ? (
                            <div className="hidden md:block">
                                <div className="mt-6 flex gap-2">
                                    <Skeleton
                                        className="h-12 w-1/3"
                                        variant="light"
                                    />
                                    <Skeleton
                                        className="h-12 w-1/3"
                                        variant="light"
                                    />
                                    <Skeleton
                                        className="h-12 w-1/3"
                                        variant="light"
                                    />
                                </div>
                            </div>
                        ) : (
                            currentUrlSearchQuery && (
                                <>
                                    <div>
                                        <div className="mt-4 flex gap-4">
                                            {Array.from({ length: 4 }).map(
                                                (_, index) => (
                                                    <Skeleton
                                                        key={index}
                                                        className="h-30 w-68"
                                                        variant="light"
                                                    />
                                                ),
                                            )}
                                        </div>
                                    </div>
                                </>
                            )
                        )}
                    </div>
                </>
            ) : (
                <>
                    <SearchPageGridHeader />
                    <SearchPageFilterTabs {...props} location={location} />
                </>
            )}

            {showMobileSearchResultsAll ? (
                <MobileSearchResultsAll
                    {...props}
                    isLoading={isLoadingFromProps}
                />
            ) : isLoadingFromProps ? (
                <div className="px-6 pt-12 md:px-0">
                    <div className="hidden md:block">
                        {currentUrlSearchQuery ? (
                            <>
                                <Skeleton
                                    className="my-6 h-10 w-full"
                                    variant="light"
                                />
                                <div className="my-6 flex gap-4">
                                    {Array.from({ length: 4 }).map(
                                        (_, index) => (
                                            <Skeleton
                                                key={index}
                                                className="h-10 w-25"
                                                variant="light"
                                            />
                                        ),
                                    )}
                                </div>
                            </>
                        ) : (
                            <div className="mb-16 flex gap-4">
                                {Array.from({ length: 2 }).map((_, index) => (
                                    <Skeleton
                                        key={index}
                                        className="h-10 w-40"
                                        variant="light"
                                    />
                                ))}
                            </div>
                        )}
                    </div>

                    <Grid
                        colClasses={gridColClasses}
                        rowsGapClass="gap-y-12 md:gap-y-20"
                    >
                        {Array.from({ length: skeletonCount }).map(
                            (_, index) => (
                                <div
                                    key={index}
                                    /*
                                     * The corner goes on the frame, not on the
                                     * skeleton: `Skeleton` carries `rounded-md`
                                     * of its own, and tailwind-merge does not
                                     * know `rounded-tile` is the same property,
                                     * so passing both leaves stylesheet order
                                     * to decide which wins. Clipping the frame
                                     * has no such argument.
                                     */
                                    className={
                                        categoryRevamp
                                            ? 'aspect-tile-portrait overflow-hidden rounded-tile'
                                            : 'aspect-tile-default'
                                    }
                                >
                                    <Skeleton
                                        className="h-full w-full"
                                        variant="light"
                                    />
                                </div>
                            ),
                        )}
                    </Grid>
                </div>
            ) : (
                <>
                    {tilesAdmin.length > 0 ? (
                        <>
                            <div className="px-6 pt-12 md:px-0">
                                <Grid
                                    colClasses={gridColClasses}
                                    rowsGapClass="gap-y-12 md:gap-y-20"
                                >
                                    {tilesAdmin.map((tile) => (
                                        <SearchPageTile
                                            key={tile.model.id}
                                            tile={{
                                                ...tile,
                                                isFollowed: isInDesignBoard(
                                                    tile.model.id,
                                                ),
                                            }}
                                        />
                                    ))}
                                </Grid>
                            </div>

                            <div className="py-20">
                                <TilePagination
                                    currentPath={location}
                                    current={pagination.page}
                                    total={pagination.totalPages}
                                    urlBuilder={buildPaginationUrl}
                                    scrollToRef={scrollToRef}
                                />
                            </div>
                        </>
                    ) : (
                        <div className="px-6 md:px-0">
                            {pageDataFromAtom && (
                                <NoMatchesFound
                                    hideFilters={true}
                                    filterStates={currentFilterStates}
                                    onDisableFilter={props.handleDisableFilter}
                                    onResetFilter={
                                        props.searchFilters.resetFilters
                                    }
                                />
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};
